import { useRef, useState, useEffect, useCallback } from 'react';
import WMIcon from './WMIcon';

/**
 * SignatureCanvas — HTML5 canvas e-signature component.
 *
 * Props:
 *   onSave({ dataUrl, signedAt, signerName }) — called on accept
 *   onCancel — called when user cancels
 *   signerNameLabel — label for name field (default: "Full Name")
 *   existingSignature — if set, shows read-only signed state
 *   readOnly
 */
export default function SignatureCanvas({
  onSave,
  onCancel,
  signerNameLabel = 'Full Name',
  existingSignature = null,
  readOnly = false,
}) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [lastPos, setLastPos] = useState(null);

  // Show existing signature in read-only mode
  useEffect(() => {
    if (existingSignature?.dataUrl && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) ctx.drawImage(img, 0, 0);
      };
      img.src = existingSignature.dataUrl;
    }
  }, [existingSignature]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = useCallback((e) => {
    if (readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getPos(e, canvas);
    setDrawing(true);
    setLastPos(pos);
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1, 0, Math.PI * 2);
    ctx.fillStyle = '#1e293b';
    ctx.fill();
    setHasStrokes(true);
  }, [readOnly]);

  const draw = useCallback((e) => {
    if (!drawing || readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);

    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    setLastPos(pos);
  }, [drawing, lastPos, readOnly]);

  const stopDraw = useCallback(() => {
    setDrawing(false);
    setLastPos(null);
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!hasStrokes) return;
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave?.({ dataUrl, signedAt: new Date().toISOString(), signerName });
  }, [hasStrokes, signerName, onSave]);

  if (existingSignature && readOnly) {
    return (
      <div className="rounded-lg border border-green-200 dark:border-green-800 overflow-hidden">
        <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 flex items-center gap-2">
          <WMIcon name="check-circle" className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="text-[var(--wm-text-ui)] font-medium text-green-700 dark:text-green-400">
            Signed by {existingSignature.signerName || 'Customer'}
            {existingSignature.signedAt && (
              <span className="font-normal ml-1 text-green-500">
                · {new Date(existingSignature.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </span>
        </div>
        <div className="bg-white dark:bg-gray-900 p-3">
          <img
            src={existingSignature.dataUrl}
            alt="Signature"
            className="max-h-20 object-contain"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Name field */}
      <div>
        <label className="text-[var(--wm-text-ui)] font-medium text-gray-600 dark:text-gray-400 block mb-1">
          {signerNameLabel}
        </label>
        <input
          type="text"
          value={signerName}
          onChange={e => setSignerName(e.target.value)}
          placeholder="Type your full name…"
          className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-[var(--wm-text-body)] focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
        />
      </div>

      {/* Canvas */}
      <div className="relative">
        <div className="absolute inset-x-4 bottom-4 border-b border-gray-300 dark:border-gray-600 pointer-events-none" />
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          className="w-full rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 touch-none cursor-crosshair"
          style={{ aspectRatio: '10/3' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {!hasStrokes && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-[var(--wm-text-ui)] text-gray-400 dark:text-gray-500">Sign above</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={clear}
          disabled={!hasStrokes}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[var(--wm-text-ui)] rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <WMIcon name="arrow-path" className="w-3.5 h-3.5" />
          Clear
        </button>
        <div className="flex-1" />
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[var(--wm-text-ui)] rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={!hasStrokes || !signerName.trim()}
          className="px-4 py-1.5 text-[var(--wm-text-ui)] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Accept &amp; Sign
        </button>
      </div>
      <p className="text-[var(--wm-text-caption)] text-gray-400 dark:text-gray-500">
        By signing, you agree to the terms of this estimate. This signature is legally binding.
      </p>
    </div>
  );
}
